import React, { useEffect, useState } from 'react';
import '../../ChildComponent.css';
import DetailRow from '@components/common/DetailRow';
import { sickChildCareData, DeathDetailsDataView, ServiceReportCodeForChild, COMMON_CONSTANTS } from './SickChildCareConstant';
import { errorLogger } from '@utils/applicationLogUtils';
import { getData } from '@services/CommonApiService';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@store/reducers';
import endpoints from '@config/endpoints';
import { formatName } from '@pages/Beneficiary-block/WomenComponent/PregnancyTracking/pregnancyUtils';
import { updatePNC } from '@store/actions/beneficiaryAction';
import { desiredFormatDate } from '@utils/commonUtils';
import { dateFormate } from '@constants/index';

interface objectType {
  isError: boolean;
  message: string;
  isDisable: boolean;
  value: any;
}

export interface formStateType {
  [key: string]: objectType;
}

const SickChildCareView = () => {
    const [sickChildCareDetails, setSickChildCareDetails] = useState<any>(null);
    const [deathDetails, setDeathDetails] = useState<any>(null);
    const [processedDeathDetails, setProcessedDeathDetails] = useState<any>(null);
    const childDetails = useSelector((state: RootState) => state.beneficiary.childDetails);
    const sickChildCareVisitDate = useSelector((state: RootState) => state.beneficiary.sickChildCareVisitDate);

    const dispatch = useDispatch();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await getData<any>(`${endpoints.CompletedServiceList}/${childDetails?.benef_id}`)
            if (res) {
                mapData(res);
                fetchDataChildDeathDetails();
            }
        } catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
        }
    };

    const fetchDataChildDeathDetails = async () => {
        try {
            const deathDetailsRes = await getData<any>(`${endpoints.childDeathDetails(childDetails?.benef_id)}`);

            if (deathDetailsRes && deathDetailsRes?.data?.length > 0) {
                const deathData = deathDetailsRes?.data?.[0];
                const processedData = deathViewData(deathData);
                setDeathDetails(deathData);
                setProcessedDeathDetails(processedData);
                
                setSickChildCareDetails((prevState: any) => {
                    return {
                        ...prevState,
                        ['deathRelatedInformation']: {
                          ...prevState['deathRelatedInformation'],
                          value: 'Yes'
                        },
                      }
                });
            } else {
                setSickChildCareDetails((prevState: any) => {
                    return {
                        ...prevState,
                        ['deathRelatedInformation']: {
                          ...prevState['deathRelatedInformation'],
                          value: 'No'
                        },
                      }
                });
            }
        } catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
        }
    };

    const mapData = (data: any) => {
        const initialState: any = {};
    
        Object.keys(JSON.parse(JSON.stringify(sickChildCareData))).forEach((key) => {
            initialState[key] = { value: null };
        });

        // Filter to only sick child care records
        const sickChildCareRecords = data?.data?.filter((item: any) =>
            item.service_code === ServiceReportCodeForChild.diarrheaTreatment ||
            item.service_code === ServiceReportCodeForChild.havingPneumonia
        ) || [];

        let targetVisitDate: string | null = null;

        if (sickChildCareVisitDate) {
            const storedDate = new Date(sickChildCareVisitDate).getTime();
            const matchingRecords = sickChildCareRecords?.filter((item: any) =>
                item.visit_date && new Date(item.visit_date).getTime() === storedDate
            );
            if (matchingRecords.length > 0) {
                targetVisitDate = matchingRecords[0].visit_date;
            }
        }

        if (!targetVisitDate) {
            targetVisitDate = sickChildCareRecords?.reduce((latest: string | null, item: any) => {
                if (!latest || (item.visit_date && new Date(item.visit_date) > new Date(latest))) {
                    return item.visit_date;
                }
                return latest;
            }, null);
        }

        const targetRecords = sickChildCareRecords?.filter((item: any) => item.visit_date === targetVisitDate);

        const updatedFormState = targetRecords.reduce((acc: any, item: any) => {
            const value = item.service_report;
            const serviceCode = item.service_code;
            switch (serviceCode) {
                case ServiceReportCodeForChild.diarrheaTreatment:
                    acc.diarrheaTreatment.value = value;
                    break;
                case ServiceReportCodeForChild.havingPneumonia:
                    acc.havingPneumonia.value = value;
                    break;
                default:
                  break;
            }
            return acc;
        }, initialState);
    
        const facilityDetailsObj = targetRecords[0] || data?.data[0];
    
        if (facilityDetailsObj) {
            const { facility_name, facility_type_name, healthprovider_f_name, healthprovider_l_name, mobiliser_f_name, mobiliser_l_name } = facilityDetailsObj;
            const healthProviderName = formatName(healthprovider_f_name, healthprovider_l_name);
            const mobiliserName = formatName(mobiliser_f_name, mobiliser_l_name);
            const localFacilityDetails = { facility_name, facility_type_name, healthProviderName, mobiliserName };
    
            dispatch(updatePNC("viewAndEditFacilityDetails", localFacilityDetails));
        }
        setSickChildCareDetails(updatedFormState);
    };

    const deathViewData = (data: any) => {
        const processedData: any = {};
        
        Object.keys(DeathDetailsDataView).forEach((key) => {
            processedData[key] = {
                ...DeathDetailsDataView[key],
                value: ''
            };
        });
        
        Object.entries(data).forEach(([key, value]) => {
            if (processedData[key]) {
                if (key === 'date_of_death') {
                    processedData['date_of_death'].value = desiredFormatDate(value, dateFormate.dd_mm_yyyy);
                    processedData['timeOfDeath'].value = desiredFormatDate(value, dateFormate.hh_mm_A);
                } else {
                    processedData[key].value = (typeof value === 'boolean') ? (value ? 'Yes' : 'No') : value;
                }
            }
        });
        
        return processedData;
    };

    const getSickChildCareDataForDisplay = () => {
        if (!sickChildCareDetails) return {};
        
        const displayData: any = {};
        if (sickChildCareData.diarrheaTreatment) {
            displayData.diarrheaTreatment = sickChildCareData.diarrheaTreatment;
        }
        if (sickChildCareData.havingPneumonia) {
            displayData.havingPneumonia = sickChildCareData.havingPneumonia;
        }
        if (sickChildCareData.deathRelatedInformation) {
            displayData.deathRelatedInformation = sickChildCareData.deathRelatedInformation;
        }
        return displayData;
    };

  return (
    <>
      <div className="child-view-content">
        <div className="child-view-details">
          <div className="child-view-header-container">{COMMON_CONSTANTS.SICK_CHILD_CARE_DETAILS}</div>
          <div className="child-details">
          {getSickChildCareDataForDisplay() && 
                    Object.keys(getSickChildCareDataForDisplay()).map((key) => {
                    const field = getSickChildCareDataForDisplay()[key];
                    return (
                        <DetailRow
                        key={key}
                        label={field["#title"]}
                        value={sickChildCareDetails?.[key]?.value}
                        />
                    );
                    })}
          </div>
        </div>
        {processedDeathDetails && <div className="child-view-details">
          <div className="child-view-header-container">Death Details</div>
          <div className="child-details">
          {processedDeathDetails && 
                    Object.keys(processedDeathDetails).map((key) => {
                    return (
                        <DetailRow
                        key={key}
                        label={processedDeathDetails?.[key]?.title}
                        value={processedDeathDetails?.[key]?.value}
                        />
                    );
                    })}
          </div>
        </div>}
      </div>
    </>
  );
};

export default SickChildCareView;
